import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Info, ChevronUp, ChevronDown, CheckCircle2 } from 'lucide-react';
import { apiPost } from '../../lib/api.js';
import { money } from './billingFormat.js';
import Toggle from './Toggle.jsx';

/**
 * "My Subscription": days remaining, auto-renew switch and a collapsible price breakdown.
 * @param {{overview: object}} props `overview` = GET /billing-center/overview
 */
export default function SubscriptionPanel({ overview }) {
  const qc = useQueryClient();
  const sub = overview.subscription || {};
  const [open, setOpen] = useState(true);
  const toggle = useMutation({
    mutationFn: (enabled) => apiPost('/billing-center/auto-renew', { enabled }),
    onSuccess: () => { toast.success('Auto-renew updated'); qc.invalidateQueries({ queryKey: ['billing-overview'] }); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not update auto-renew'),
  });
  const expires = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleString() : null;

  return (
    <section className="panel p-5" data-testid="subscription-panel">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Info className="w-5 h-5 text-civix-purpleSoft" /> My Subscription</h2>
        {sub.daysRemaining != null && (
          <div className="text-right">
            <div className="text-[11px] text-white/50">Days Remaining</div>
            <div className="text-2xl font-bold">{sub.daysRemaining}</div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-4 text-sm">
        <div className="text-white/70">
          <div className="font-medium text-white">{sub.planName || 'No plan'}{sub.status ? <span className="ml-2 text-[10px] uppercase tracking-wider text-white/50">{sub.status}</span> : null}</div>
          {sub.comped ? (
            <p className="text-xs mt-1">CRM access is free during beta{sub.promoCode ? ` (promo ${sub.promoCode})` : ''}. You only pay for usage from your account balance.</p>
          ) : expires ? (
            <p className="text-xs mt-1">Your subscription {sub.autoRenew ? 'renews' : 'expires'} {expires}.</p>
          ) : null}
        </div>
        {sub.canToggleAutoRenew && overview.isAdmin && (
          <div className="text-right shrink-0">
            <div className="text-[11px] text-white/50 mb-1">Auto-renew my subscription</div>
            <Toggle checked={sub.autoRenew} disabled={toggle.isPending} onChange={(v) => toggle.mutate(v)} label="Auto-renew my subscription" />
          </div>
        )}
      </div>

      {!sub.comped && (
        <>
          <button type="button" onClick={() => setOpen((o) => !o)} className="mt-4 text-xs font-semibold text-civix-purpleSoft flex items-center gap-1">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} SUBSCRIPTION INFO
          </button>
          {open && (
            <div className="mt-2 rounded-lg border border-white/10 p-4 text-sm max-w-md">
              <div className="flex justify-between"><span>Subscription</span><span>{money(sub.priceUsd)}</span></div>
              <div className="flex justify-between border-t border-white/10 mt-3 pt-3 font-semibold"><span>Total</span><span>{money(sub.priceUsd)}</span></div>
              <p className="text-[11px] text-white/40 mt-2">Sales tax, if any, is calculated at checkout from your billing address.</p>
              {sub.features?.length > 0 && (
                <ul className="text-xs text-white/60 mt-3 space-y-1">
                  {sub.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {sub.autoRenew && (
            <p className="text-xs text-emerald-300/80 mt-4">Your subscription will automatically renew and your default payment method will be charged {money(sub.priceUsd)} at the end of the current period.</p>
          )}
        </>
      )}

      <div className="mt-4 rounded-lg border border-civix-purple/40 bg-civix-purple/5 p-3 text-xs text-white/70 flex gap-2">
        <Info className="w-4 h-4 text-civix-purpleSoft shrink-0" />
        Funds you add to your account cannot be used for subscription dues, which are billed monthly to your default payment method.
      </div>
    </section>
  );
}
